import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import type { SiteSnapshot, TsPoint } from "@/data/types";
import { baseOption, chartTokens, hexA } from "@/lib/chartTheme";
import { prepareLineTuples, type EChartsLinePoint } from "@/lib/chartGaps";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { useT } from "@/lib/i18n";

/**
 * Stacked-line spark chart showing power flows over time.
 *
 * Series (canonical display sign — positive = charge/import, matching
 * lib/format + RightNowStrip + the on-page legend):
 *   - Grid (positive = import, grid → site)
 *   - Battery (positive = charging — R01: previously negated to a
 *     "+into-bus" frame, which contradicted the strip's "+ = charge"
 *     legend on the same Overview fold)
 *   - PV generation (only when the topology has PV)
 *   - Net building load (dashed outline, no fill — "where energy goes")
 *
 * Two data shapes accepted:
 *
 *   1. `snapshots: SiteSnapshot[]` — used by live mode (the `recent[]`
 *      buffer in the store). Each snapshot carries every field on the
 *      same ts, so all four series share one ts grid.
 *
 *   2. `seriesData: { grid, battery, pv, load }` (each TsPoint[]) — used
 *      by historical mode (Overview's last-72h chart). Each series has
 *      its OWN ts array. The EMS server's AVG aggregation may emit
 *      slightly different timestamps per key (e.g. grid at the bucket
 *      end, derived load at the bucket start); keeping per-series ts
 *      arrays lets each line render at its own grid without phantom
 *      zero-crossings between buckets — the bug the pseudo-snapshot
 *      approach introduced.
 *
 * Values are in WATTS (multiply by 0.001 inside this component). Battery
 * plots on the canonical positive=charge sign (R01) — the same as every
 * other live-flow surface; no render-time sign flip.
 *
 * Includes mouse-wheel zoom + a bottom slider so the operator can drill
 * into any sub-window.
 */
export interface PowerSparkSeriesData {
  /** Grid power, canonical (positive = import). Watts. */
  grid: TsPoint[];
  /** BESS aggregate power, canonical (positive = charge). Watts. */
  battery: TsPoint[];
  /** PV generation. Watts. Pass [] when no PV. */
  pv: TsPoint[];
  /** Building load. Watts. */
  load: TsPoint[];
}

export function PowerSparkChart({
  snapshots,
  seriesData,
  height = 220,
  hasPv,
}: {
  snapshots?: SiteSnapshot[];
  seriesData?: PowerSparkSeriesData;
  height?: number;
  hasPv: boolean;
}) {
  // Bumps when [data-theme] flips so chartTokens() are re-read and the
  // option repaints in the new theme's colours (DSGN-07).
  const themeVersion = useThemeVersion();
  const tt = useT();
  const option = useMemo(() => {
    const base = baseOption();
    const t = chartTokens();

    // Pick per-series arrays. Per-series mode (seriesData) wins when
    // present so each line has its own ts grid; otherwise materialise
    // from the snapshot buffer in W→kW units. Every series is then
    // passed through the unified gap-handling helper so short/medium
    // holes get bridged with synthetic samples and real outages
    // produce a clean break instead of a misleading straight line.
    const rawGrid: [number, number][] = seriesData
      ? seriesData.grid.map((p) => [p.ts, p.value / 1000])
      : (snapshots ?? []).map((s) => [s.ts, s.grid.active_W / 1000]);
    // R01: plot battery on the app's CANONICAL display sign (positive =
    // charging) — the same convention RightNowStrip / KPIs / FlowEdge / the
    // on-page legend / lib/format all use. The chart previously negated this
    // to a "+into-bus" frame, so the same "+" meant CHARGE in the strip and
    // DISCHARGE in the chart on one Overview fold — the GM comprehension trap.
    const rawBattery: [number, number][] = seriesData
      ? seriesData.battery.map((p) => [p.ts, p.value / 1000])
      : (snapshots ?? []).map((s) => [s.ts, s.bess_aggregate.active_W / 1000]);
    const rawPv: [number, number][] = seriesData
      ? seriesData.pv.map((p) => [p.ts, p.value / 1000])
      : (snapshots ?? []).map((s) => [s.ts, (s.pv?.active_W ?? 0) / 1000]);
    const rawLoad: [number, number][] = seriesData
      ? seriesData.load.map((p) => [p.ts, p.value / 1000])
      : (snapshots ?? []).map((s) => [s.ts, s.building_load_W / 1000]);

    const grid = prepareLineTuples(rawGrid);
    const battery = prepareLineTuples(rawBattery);
    const pv = prepareLineTuples(rawPv);
    const load = prepareLineTuples(rawLoad);

    const series: object[] = [
      area(tt("monitor.now.grid"),    grid,    t.import,    0.26),
      area(tt("monitor.now.battery"), battery, t.discharge, 0.24),
      ...(hasPv ? [area(tt("monitor.now.solar"), pv, t.solar, 0.30)] : []),
      // Net building load wears its own neutral token — never a grid
      // DIRECTION colour (it used to wear grid-export green; DSGN-05/08).
      line(tt("monitor.now.netBuildingLoad"), load, t.net, "dashed"),
    ];
    return {
      ...base,
      grid: { ...base.grid, top: 30, bottom: 36, left: 56, right: 16, containLabel: true },
      series,
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none", zoomOnMouseWheel: true, moveOnMouseWheel: false, throttle: 50 },
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "none",
          bottom: 2,
          height: 16,
          showDetail: false,
          borderColor: t.tooltipBorder,
          backgroundColor: hexA(t.textFaint, 0.06),
          fillerColor: hexA(t.accent, 0.12),
          handleStyle: { color: t.accent, borderColor: t.accent },
          moveHandleStyle: { color: t.accent },
          dataBackground: {
            lineStyle: { color: hexA(t.textFaint, 0.4), width: 1 },
            areaStyle: { color: hexA(t.textFaint, 0.08) },
          },
          selectedDataBackground: {
            lineStyle: { color: t.accent, width: 1 },
            areaStyle: { color: hexA(t.accent, 0.18) },
          },
        },
      ],
      tooltip: {
        ...(base.tooltip as object),
        valueFormatter: (v: number | string | null) => {
          if (v == null) return "—";
          const n = typeof v === "number" ? v : Number(v);
          return Number.isFinite(n) ? `${n.toFixed(1)} kW` : "—";
        },
      },
    } as object;
  }, [snapshots, seriesData, hasPv, themeVersion, tt]);
  return <ReactECharts option={option} notMerge style={{ height }} lazyUpdate />;
}

function area(name: string, data: EChartsLinePoint[], color: string, alpha: number) {
  return {
    name,
    type: "line",
    showSymbol: false,
    smooth: 0.28,
    sampling: "lttb",
    // Honest gap policy supplied by prepareLineTuples — explicit nulls
    // break the line at real outages, intermediate synthetic samples
    // keep medium holes visually continuous.
    connectNulls: false,
    data,
    // `lineStyle` colours the stroke; `itemStyle` colours the legend
    // marker dot. ECharts falls back to a default palette for the legend
    // when itemStyle.color is missing — which was causing the "Battery
    // contribution" and "Building load" legend dots to render in swapped
    // colours.
    lineStyle: { color, width: 2.2 },
    itemStyle: { color },
    areaStyle: {
      opacity: 1,
      color: {
        type: "linear", x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0,    color: hexA(color, alpha) },
          { offset: 0.55, color: hexA(color, alpha * 0.4) },
          { offset: 1,    color: hexA(color, 0)    },
        ],
      },
    },
    emphasis: { focus: "series" },
  };
}

function line(name: string, data: EChartsLinePoint[], color: string, type: "solid" | "dashed" = "solid") {
  return {
    name,
    type: "line",
    showSymbol: false,
    smooth: 0.28,
    sampling: "lttb",
    connectNulls: false,
    data,
    lineStyle: { color, width: 1.6, type },
    itemStyle: { color },   // see comment in area()
    emphasis: { focus: "series" },
  };
}
